/**
 * Bot athletes. A bot brain turns a skill level into presses fed to the very same sim as a
 * human, so bots obey every rule (alternation, fouls, the attempt clock) and their results
 * are produced by the physics, not looked up.
 */
import type { BotLevel } from './protocol.js';
import { HJ_IDEAL_ANGLE, LJ_IDEAL_ANGLE } from './physics.js';
import { simAction, simPress, type AthleteSim } from './sim/athlete.js';
import { PV_PLANT_BEFORE_BAR } from './sim/jump.js';
import { idealThrowAngle } from './sim/throw.js';
import type { RunKey } from './input.js';

export const BOT_NAMES = [
  'Bolt Bot',
  'Dash Bot',
  'Leap Bot',
  'Hurl Bot',
  'Vault Bot',
  'Pace Bot',
  'Sprint Bot',
  'Glide Bot',
];

interface Skill {
  /** Target alternation cadence (presses/s). */
  cadence: number;
  /** Relative jitter on every interval. */
  jitter: number;
  /** Reaction time after the gun (s). */
  reaction: number;
  /** Standard deviation of the take-off point (m) and the angle (deg). */
  placeErr: number;
  angleErr: number;
  /** Distance-event pacing: cadence held before the final sprint. */
  pace: number;
}

const SKILLS: Record<BotLevel, Skill> = {
  club: { cadence: 6.5, jitter: 0.22, reaction: 0.35, placeErr: 0.9, angleErr: 12, pace: 5.5 },
  county: { cadence: 8, jitter: 0.16, reaction: 0.28, placeErr: 0.6, angleErr: 8, pace: 6.5 },
  national: { cadence: 9.4, jitter: 0.12, reaction: 0.2, placeErr: 0.35, angleErr: 5, pace: 7.5 },
  olympic: { cadence: 10.8, jitter: 0.1, reaction: 0.15, placeErr: 0.2, angleErr: 3, pace: 8.2 },
};

export interface BotBrain {
  level: BotLevel;
  rng: () => number;
  /** Event time of the next press. */
  nextPress: number;
  key: RunKey;
  /** Per-attempt plan for field events. */
  plan: { attempt: number; takeoff: number; hold: number; pressedAt: number | null } | null;
  /** Per-event random offsets so two bots at the same level differ. */
  cadenceScale: number;
  /** Event time the sim was last advanced to (its `x` is valid at this time). */
  lastT: number;
}

export function createBot(level: BotLevel, rng: () => number): BotBrain {
  return {
    level,
    rng,
    nextPress: 0,
    key: 0,
    plan: null,
    cadenceScale: 0.9 + rng() * 0.2,
    lastT: 0,
  };
}

/** Turn-based events: the bot's clock and plan restart with its turn. */
export function botBeginTurn(b: BotBrain, t: number): void {
  b.lastT = t;
  b.nextPress = t;
  b.plan = null;
}

function gauss(rng: () => number): number {
  // Box-Muller
  const u = Math.max(1e-9, rng());
  const v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Generate this bot's inputs up to event time `until`. */
export function stepBot(b: BotBrain, sim: AthleteSim, until: number): void {
  const sk = SKILLS[b.level];
  if (sim.done) return;
  if (sim.kind === 'run') {
    const cfg = sim.cfg;
    // Wait for the gun plus reaction.
    if (b.nextPress < sk.reaction) b.nextPress = sk.reaction + b.rng() * 0.05;
    while (b.nextPress <= until) {
      const s = sim.state;
      if (s.finished) return;
      let cadence = sk.cadence * b.cadenceScale;
      if (cfg.stamina) {
        // Pace: hold a sustainable rhythm and kick for the last 150 m.
        const left = cfg.distance - s.x;
        cadence =
          left < (cfg.distance === 400 ? 60 : 80) && s.stamina > 0.35
            ? sk.cadence * b.cadenceScale
            : Math.min(cfg.stamina.sustainable * 0.9, sk.pace * b.cadenceScale);
      }
      simPress(sim, b.key, b.nextPress);
      b.key = b.key === 0 ? 1 : 0;
      b.nextPress += (1 / cadence) * Math.max(0.4, 1 + gauss(b.rng) * sk.jitter);
    }
    if (
      cfg.hurdles &&
      sim.state.hurdle < 10 &&
      sim.state.jumpAt === null &&
      13.72 + sim.state.hurdle * 9.14 - sim.state.x < sim.state.v * 0.5
    )
      simAction(sim, true, until);
    b.lastT = until;
    return;
  }
  if (sim.kind === 'sp') {
    // Shot put: wind up for a couple of seconds, then Space and hold for the angle.
    const s = sim.state;
    if (!b.plan || b.plan.attempt !== s.attempt) {
      b.plan = {
        attempt: s.attempt,
        takeoff: 1.6 + b.rng() * 0.8 + (sk.cadence < 8 ? 0.6 : 0), // wind-up seconds
        hold: Math.max(
          0.08,
          (idealThrowAngle(sim.cfg) + gauss(b.rng) * sk.angleErr) / sim.cfg.gaugeRate,
        ),
        pressedAt: null,
      };
      b.nextPress = sim.attemptStart + 0.5 + b.rng() * 0.5;
    }
    const plan = b.plan;
    const windupEnd = sim.attemptStart + 0.5 + plan.takeoff;
    while (b.nextPress <= until && s.phase === 'power') {
      if (sim.cfg.discipline === 'javelin' ? s.runX >= 24 : b.nextPress >= windupEnd) {
        plan.pressedAt = b.nextPress;
        simAction(sim, true, b.nextPress);
        break;
      }
      simPress(sim, b.key, b.nextPress);
      b.key = b.key === 0 ? 1 : 0;
      b.nextPress +=
        (1 / (sk.cadence * b.cadenceScale)) * Math.max(0.4, 1 + gauss(b.rng) * sk.jitter);
    }
    if (
      s.phase === 'gauge' &&
      plan.pressedAt !== null &&
      until >= plan.pressedAt + plan.hold &&
      (sim.cfg.discipline !== 'discus' || Math.abs(s.heading) < 14)
    )
      simAction(sim, false, until);
    b.lastT = until;
    return;
  }
  // Jumps: run up, press Space at the planned point, hold for the planned angle.
  const s = sim.state;
  const attempt = sim.kind === 'lj' ? sim.state.attempt : sim.state.log.length;
  if (!b.plan || b.plan.attempt !== attempt) {
    const isLj = sim.kind === 'lj';
    const target =
      sim.kind === 'lj'
        ? sim.cfg.board - 0.15
        : sim.cfg.bar - (sim.cfg.vault ? PV_PLANT_BEFORE_BAR : 2.6);
    const ideal = isLj ? LJ_IDEAL_ANGLE : HJ_IDEAL_ANGLE;
    b.plan = {
      attempt,
      takeoff:
        target +
        gauss(b.rng) * (sim.kind === 'hj' && sim.cfg.vault ? sk.placeErr * 0.3 : sk.placeErr) -
        (isLj ? sk.placeErr * 0.5 : 0),
      hold: Math.max(0.08, (ideal + gauss(b.rng) * sk.angleErr) / sim.cfg.gaugeRate),
      pressedAt: null,
    };
    b.nextPress = sim.attemptStart + 0.6 + b.rng() * 0.6;
  }
  const plan = b.plan;
  // The sim's x is as of `lastT`; extrapolate at the current speed to hit the planned
  // take-off point between presses rather than a stride past it.
  if (s.phase === 'runup' && plan.pressedAt === null && s.runner.v > 0.5) {
    const eta = b.lastT + (plan.takeoff - s.x) / s.runner.v;
    if (eta <= until) {
      const at = Math.max(b.lastT, eta);
      plan.pressedAt = at;
      simAction(sim, true, at);
    }
  }
  while (b.nextPress <= until) {
    if (s.phase !== 'runup') break;
    simPress(sim, b.key, b.nextPress);
    b.key = b.key === 0 ? 1 : 0;
    b.nextPress +=
      (1 / (sk.cadence * b.cadenceScale)) * Math.max(0.4, 1 + gauss(b.rng) * sk.jitter);
  }
  if (s.phase === 'planted' && plan.pressedAt !== null && until >= plan.pressedAt + 0.85)
    simAction(sim, true, until);
  if (s.phase === 'gauge' && plan.pressedAt !== null && until >= plan.pressedAt + plan.hold) {
    simAction(sim, false, plan.pressedAt + plan.hold);
  }
  b.lastT = until;
}
